/**
 * Demo Account Seeder
 *
 * Creates pre-populated demo accounts with realistic progress data.
 * Used for testing and demonstration purposes.
 *
 * Refactored into demo/ subfolder to prevent god-file anti-pattern.
 * - Helpers: src/accounts/logic/demo/helpers.ts
 * - Profiles: src/accounts/logic/demo/learnerProfiles.ts
 * - Assessments: src/accounts/logic/demo/assessmentSeeder.ts
 */

import { MultiAccountStateV4 } from './migration'
import { linkParentToLearner, linkTeacherToLearner } from './relationships'
import { AccountsState, Classroom } from '../types'
import { AppState } from '../../app/types'
import { createRng } from '../../utils/deterministicRng'
import { debug } from '../../utils/logger'

import {
  DEMO_SEED,
  createDemoParent,
  createDemoTeacher,
  createDemoAdmin,
  createDemoClassroom,
} from './demo/helpers'
import { createAllLearners } from './demo/learnerProfiles'
export { seedDemoAssessments } from './demo/assessmentSeeder'

/**
 * Generate complete demo account state with realistic data.
 */
export function generateDemoAccounts(): MultiAccountStateV4 {
  const rng = createRng(DEMO_SEED)

  // 1. Create Learners
  const { djayden, emma, noah, sophie, liam, noor, yara, sem } =
    createAllLearners(rng)

  // 2. Create Parents
  const mama = createDemoParent('Mama', 'mama@voorbeeld.nl')
  const papaNoah = createDemoParent('Papa van Noah', 'papa.noah@voorbeeld.nl')
  const mamaNoor = createDemoParent('Mama van Noor', 'mama.noor@voorbeeld.nl')

  // 3. Create Teachers
  const meesterJay = createDemoTeacher(
    'Meester Jay',
    'Basisschool De Regenboog',
    'jay@regenboog.nl',
  )
  const jufLisa = createDemoTeacher(
    'Juf Lisa',
    'Basisschool Het Kompas',
    'lisa@hetkompas.nl',
  )
  const jufMarieke = createDemoTeacher(
    'Juf Marieke',
    'Basisschool De Zonnewijzer',
    'marieke@zonnewijzer.nl',
  )

  // 4. Create Admin
  const admin = createDemoAdmin('Admin', 'admin@meesterdjay.nl')

  // 5. Create Classrooms
  const classJay = createDemoClassroom(meesterJay, 'Groep 7A', 7, [
    djayden.account.id,
    emma.account.id,
    noah.account.id,
  ])

  const classLisa = createDemoClassroom(jufLisa, 'Groep 8B', 8, [
    sophie.account.id,
    liam.account.id,
    noor.account.id,
  ])

  const classMarieke = createDemoClassroom(jufMarieke, 'Groep 6A', 6, [
    yara.account.id,
    sem.account.id,
  ])

  // Build top-level classroom registry
  const demoClassrooms: Record<string, Classroom> = {
    [classJay.id]: classJay,
    [classLisa.id]: classLisa,
    [classMarieke.id]: classMarieke,
  }

  // Build learnerToClassrooms reverse index
  const demoL2C: Record<string, string[]> = {}
  for (const classroom of [classJay, classLisa, classMarieke]) {
    for (const learnerId of classroom.learnerIds) {
      if (!demoL2C[learnerId]) demoL2C[learnerId] = []
      demoL2C[learnerId].push(classroom.id)
    }
  }

  // 6. Build Accounts State
  let accountsState: AccountsState = {
    accountsById: {
      [djayden.account.id]: djayden.account,
      [emma.account.id]: emma.account,
      [noah.account.id]: noah.account,
      [sophie.account.id]: sophie.account,
      [liam.account.id]: liam.account,
      [noor.account.id]: noor.account,
      [yara.account.id]: yara.account,
      [sem.account.id]: sem.account,
      [mama.id]: mama,
      [papaNoah.id]: papaNoah,
      [mamaNoor.id]: mamaNoor,
      [meesterJay.id]: meesterJay,
      [jufLisa.id]: jufLisa,
      [jufMarieke.id]: jufMarieke,
      [admin.id]: admin,
    },
    activeAccountId: djayden.account.id,
    relationships: {
      parentToLearners: {},
      learnerToParents: {},
      teacherToLearners: {},
      learnerToTeachers: {},
      learnerToClassrooms: demoL2C,
    },
    classrooms: demoClassrooms,
  }

  // Set demo linkCodes
  djayden.account.linkCode = 'DEMO01'
  emma.account.linkCode = 'DEMO02'
  noah.account.linkCode = 'DEMO03'
  sophie.account.linkCode = 'DEMO04'
  liam.account.linkCode = 'DEMO05'
  noor.account.linkCode = 'DEMO06'
  yara.account.linkCode = 'DEMO07'
  sem.account.linkCode = 'DEMO08'

  // Set Djayden as active
  djayden.account.lastActive = Date.now()

  // 7. Link Relationships
  accountsState = linkParentToLearner(
    accountsState,
    mama.id,
    djayden.account.id,
  )
  accountsState = linkParentToLearner(accountsState, mama.id, emma.account.id)
  accountsState = linkParentToLearner(
    accountsState,
    papaNoah.id,
    noah.account.id,
  )
  accountsState = linkParentToLearner(
    accountsState,
    mamaNoor.id,
    noor.account.id,
  )

  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    djayden.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    emma.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    meesterJay.id,
    noah.account.id,
  )

  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    sophie.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    liam.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufLisa.id,
    noor.account.id,
  )

  accountsState = linkTeacherToLearner(
    accountsState,
    jufMarieke.id,
    yara.account.id,
  )
  accountsState = linkTeacherToLearner(
    accountsState,
    jufMarieke.id,
    sem.account.id,
  )

  // 8. Build Learner Data Map
  const learnerData: Record<string, AppState> = {
    [djayden.account.id]: djayden.data,
    [emma.account.id]: emma.data,
    [noah.account.id]: noah.data,
    [sophie.account.id]: sophie.data,
    [liam.account.id]: liam.data,
    [noor.account.id]: noor.data,
    [yara.account.id]: yara.data,
    [sem.account.id]: sem.data,
  }

  const demoState: MultiAccountStateV4 = {
    schemaVersion: 4,
    accounts: accountsState,
    learnerData,
  }

  debug('✨ Demo accounts created (all near level-up for testing):')
  debug(`   📚 Basisschool De Regenboog (Groep 7A)`)
  debug(`   📚 Basisschool Het Kompas (Groep 8B)`)
  debug(`   📚 Basisschool De Zonnewijzer (Groep 6A)`)
  debug(`   👪 3 Ouders, 🛡️ 1 Admin`)

  return demoState
}
